import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, isAfter, isBefore, parseISO, startOfDay, endOfDay } from 'date-fns';
import {
  Building2,
  ClipboardList,
  Filter,
  Plus,
  Search,
  Users,
  BarChart3,
  KanbanSquare,
  X,
  Download,
  Mail,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PilotCrmService } from '@/services/pilot-crm.service';
import { OperationsUserService } from '@/services/operations-user.service';
import { EmployerLead, PilotLeadPriority, PilotLeadSource, PilotLeadStatus } from '@/models/pilot-crm.model';
import {
  EMPLOYEE_COUNT_RANGES,
  PILOT_LEAD_PRIORITIES,
  PILOT_LEAD_SOURCES,
  PILOT_LEAD_STATUSES,
} from '@/data/pilot-crm-constants';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormInput,
  FormSelect,
  FormTextarea,
  LoadingSpinner,
  SectionHeader,
} from '@/components/common';
import { LeadQuickActions } from '@/components/pilot-crm/LeadQuickActions';
import { EmailComposeModal } from '@/components/pilot-crm/EmailComposeModal';

const isPilotCrmEditor = (role?: string | null) => {
  if (!role) return false;
  const normalized = role === 'operationsAdmin' ? 'operations_admin' : role;
  return ['operations_admin', 'executive', 'support'].includes(normalized);
};

export const PilotCrmLeads: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const canEdit = isPilotCrmEditor(profile?.role);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PilotLeadStatus>(
    (searchParams.get('status') as PilotLeadStatus) || 'all'
  );
  const [sourceFilter, setSourceFilter] = useState<'all' | PilotLeadSource>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | PilotLeadPriority>('all');
  const [sortBy, setSortBy] = useState<'updatedAt' | 'nextActionAt' | 'createdAt'>('updatedAt');
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<'no_change' | PilotLeadStatus>('no_change');
  const [bulkOwner, setBulkOwner] = useState<'no_change' | string>('no_change');
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<'all' | string>('all');
  const [emailModalLead, setEmailModalLead] = useState<EmployerLead | null>(null);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status && PILOT_LEAD_STATUSES.includes(status as PilotLeadStatus)) {
      setStatusFilter(status as PilotLeadStatus);
    }
  }, [searchParams]);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['pilot-crm-leads'],
    queryFn: () => PilotCrmService.getLeads(),
    enabled: !!profile,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['operations-users'],
    queryFn: () => OperationsUserService.getActiveUsers(),
    enabled: !!profile,
  });

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => map.set(user.id, user.fullName));
    return map;
  }, [users]);

  const filteredLeads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = leads.filter((lead) => {
      if (statusFilter !== 'all' && lead.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && lead.source !== sourceFilter) return false;
      if (priorityFilter !== 'all' && lead.priority !== priorityFilter) return false;
      if (ownerFilter !== 'all' && lead.ownerUserId !== ownerFilter) return false;

      if (dateRangeStart) {
        const startDate = startOfDay(parseISO(dateRangeStart));
        if (isBefore(lead.createdAt, startDate)) return false;
      }
      if (dateRangeEnd) {
        const endDate = endOfDay(parseISO(dateRangeEnd));
        if (isAfter(lead.createdAt, endDate)) return false;
      }

      if (!query) return true;
      return (
        lead.companyName.toLowerCase().includes(query) ||
        lead.contactEmail.toLowerCase().includes(query) ||
        (lead.contactName && lead.contactName.toLowerCase().includes(query)) ||
        (lead.industry && lead.industry.toLowerCase().includes(query))
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'nextActionAt') {
        const aTime = a.nextActionAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bTime = b.nextActionAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      }
      if (sortBy === 'createdAt') {
        return b.createdAt.getTime() - a.createdAt.getTime();
      }
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    });

    return sorted;
  }, [leads, searchQuery, statusFilter, sourceFilter, priorityFilter, sortBy, ownerFilter, dateRangeStart, dateRangeEnd]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (sourceFilter !== 'all') count++;
    if (priorityFilter !== 'all') count++;
    if (ownerFilter !== 'all') count++;
    if (dateRangeStart || dateRangeEnd) count++;
    return count;
  }, [statusFilter, sourceFilter, priorityFilter, ownerFilter, dateRangeStart, dateRangeEnd]);

  const clearAllFilters = () => {
    setStatusFilter('all');
    setSourceFilter('all');
    setPriorityFilter('all');
    setOwnerFilter('all');
    setDateRangeStart('');
    setDateRangeEnd('');
    setSearchQuery('');
    setSearchParams({});
  };

  const exportLeads = () => {
    const headers = ['Company', 'Contact', 'Email', 'Phone', 'Status', 'Source', 'Priority', 'Created'];
    const rows = filteredLeads.map(lead => [
      lead.companyName,
      lead.contactName || '',
      lead.contactEmail,
      lead.phone || '',
      lead.status,
      lead.source,
      lead.priority,
      format(lead.createdAt, 'yyyy-MM-dd'),
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pilot-crm-leads-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) {
        throw new Error('Missing user profile');
      }
      const updates: { status?: PilotLeadStatus; ownerUserId?: string } = {};
      if (bulkStatus !== 'no_change') {
        updates.status = bulkStatus;
      }
      if (bulkOwner !== 'no_change') {
        updates.ownerUserId = bulkOwner;
      }
      await PilotCrmService.bulkUpdateLeads(selectedLeadIds, updates, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
      setSelectedLeadIds([]);
    },
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLeadIds(filteredLeads.map((lead) => lead.id));
    } else {
      setSelectedLeadIds([]);
    }
  };

  const handleSelectLead = (leadId: string, checked: boolean) => {
    if (checked) {
      setSelectedLeadIds((prev) => [...prev, leadId]);
    } else {
      setSelectedLeadIds((prev) => prev.filter((id) => id !== leadId));
    }
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading pilot leads..." fullScreen />;
  }

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...PILOT_LEAD_STATUSES.map((status) => ({
      value: status,
      label: status.replace('_', ' '),
    })),
  ];

  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    ...PILOT_LEAD_SOURCES.map((source) => ({
      value: source,
      label: source.replace('_', ' '),
    })),
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    ...PILOT_LEAD_PRIORITIES.map((priority) => ({
      value: priority,
      label: priority,
    })),
  ];

  const sortOptions = [
    { value: 'updatedAt', label: 'Updated' },
    { value: 'nextActionAt', label: 'Next Action' },
    { value: 'createdAt', label: 'Created' },
  ];

  const ownerOptions = [
    { value: 'all', label: 'All Owners' },
    ...users.map((user) => ({ value: user.id, label: user.fullName })),
  ];

  const bulkOwnerOptions = [
    { value: 'no_change', label: 'No change' },
    ...users.map((user) => ({ value: user.id, label: user.fullName })),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        icon={ClipboardList}
        title="Pilot CRM"
        description="Founding Employer Pilot lead tracking"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" icon={BarChart3} onClick={() => navigate('/pilot-crm/dashboard')}>
              Dashboard
            </Button>
            <Button variant="outline" icon={KanbanSquare} onClick={() => navigate('/pilot-crm/kanban')}>
              Kanban
            </Button>
            <Button variant="outline" onClick={() => navigate('/pilot-crm/tasks')}>
              Tasks
            </Button>
            {canEdit && (
              <Button variant="primary" icon={Plus} onClick={() => setShowNewLeadModal(true)}>
                New Lead
              </Button>
            )}
          </div>
        }
      />

      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
            {activeFiltersCount > 0 && (
              <Badge variant="info" size="sm">{activeFiltersCount} active</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" icon={X} onClick={clearAllFilters}>
                Clear all
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'More filters'}
            </Button>
            <Button variant="outline" size="sm" icon={Download} onClick={exportLeads}>
              Export
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormInput
            label="Search"
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Company, contact, email..."
          />
          <FormSelect
            label="Status"
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              if (e.target.value !== 'all') {
                setSearchParams({ status: e.target.value });
              } else {
                setSearchParams({});
              }
            }}
          />
          <FormSelect
            label="Source"
            options={sourceOptions}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as any)}
          />
          <FormSelect
            label="Priority"
            options={priorityOptions}
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as any)}
          />
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
            <FormSelect
              label="Owner"
              options={ownerOptions}
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
            />
            <FormInput
              label="Created From"
              type="date"
              value={dateRangeStart}
              onChange={(e) => setDateRangeStart(e.target.value)}
            />
            <FormInput
              label="Created To"
              type="date"
              value={dateRangeEnd}
              onChange={(e) => setDateRangeEnd(e.target.value)}
            />
            <FormSelect
              label="Sort By"
              options={sortOptions}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
            />
          </div>
        )}
      </Card>

      {selectedLeadIds.length > 0 && (
        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-sm font-medium text-gray-700">
              {selectedLeadIds.length} selected
            </div>
            {canEdit && (
              <>
                <FormSelect
                  label="Set Status"
                  options={[
                    { value: 'no_change', label: 'No change' },
                    ...PILOT_LEAD_STATUSES.map((status) => ({
                      value: status,
                      label: status.replace('_', ' '),
                    })),
                  ]}
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value as any)}
                />
                <FormSelect
                  label="Assign Owner"
                  options={bulkOwnerOptions}
                  value={bulkOwner}
                  onChange={(e) => setBulkOwner(e.target.value)}
                />
                <Button
                  variant="primary"
                  onClick={() => bulkMutation.mutate()}
                  isLoading={bulkMutation.isPending}
                >
                  Apply
                </Button>
              </>
            )}
          </div>
        </Card>
      )}

      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Leads ({filteredLeads.length})
          </h3>
        </div>
        {filteredLeads.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No leads found"
            description={
              searchQuery || statusFilter !== 'all' || sourceFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first lead'
            }
            action={
              canEdit && !searchQuery && statusFilter === 'all' && sourceFilter === 'all' && priorityFilter === 'all' ? (
                <Button variant="primary" icon={Plus} onClick={() => setShowNewLeadModal(true)}>
                  New Lead
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                <tr>
                  <th className="px-6 py-3">
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.length > 0 && selectedLeadIds.length === filteredLeads.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th className="px-6 py-3">Company</th>
                  <th className="px-6 py-3">Contact</th>
                  <th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3">Employees</th>
                  <th className="px-6 py-3">Source</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Priority</th>
                  <th className="px-6 py-3">Next Action</th>
                  <th className="px-6 py-3">Owner</th>
                  <th className="px-6 py-3">Updated</th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-blue-50/30 cursor-pointer"
                    onClick={() => navigate(`/pilot-crm/leads/${lead.id}`)}
                  >
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedLeadIds.includes(lead.id)}
                        onChange={(e) => handleSelectLead(lead.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        {lead.companyName}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{lead.contactName || '-'}</td>
                    <td className="px-6 py-4 text-gray-700">{lead.contactEmail}</td>
                    <td className="px-6 py-4 text-gray-700">{lead.employeeCountRange || '-'}</td>
                    <td className="px-6 py-4 text-gray-700">{lead.source.replace('_', ' ')}</td>
                    <td className="px-6 py-4">
                      <Badge variant="info" size="sm">
                        {lead.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={lead.priority === 'high' ? 'warning' : 'gray'} size="sm">
                        {lead.priority}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {lead.nextActionAt ? format(lead.nextActionAt, 'MMM d, yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {lead.ownerUserId ? userNameById.get(lead.ownerUserId) || 'Unknown' : 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {format(lead.updatedAt, 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <LeadQuickActions
                        lead={lead}
                        compact
                        onEmailClick={() => setEmailModalLead(lead)}
                        onCallClick={() => lead.phone && window.open(`tel:${lead.phone}`)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showNewLeadModal && (
        <NewLeadModal
          onClose={() => setShowNewLeadModal(false)}
          onCreated={() => {
            setShowNewLeadModal(false);
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
          }}
          ownerOptions={[
            { value: 'unassigned', label: 'Unassigned' },
            ...users.map((user) => ({ value: user.id, label: user.fullName })),
          ]}
        />
      )}

      {emailModalLead && (
        <EmailComposeModal
          lead={emailModalLead}
          onClose={() => setEmailModalLead(null)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
          }}
        />
      )}
    </div>
  );
};

interface NewLeadModalProps {
  onClose: () => void;
  onCreated: () => void;
  ownerOptions: Array<{ value: string; label: string }>;
}

const NewLeadModal: React.FC<NewLeadModalProps> = ({ onClose, onCreated, ownerOptions }) => {
  const { profile } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    employeeCountRange: '',
    website: '',
    hqState: '',
    contactName: '',
    contactEmail: '',
    contactRole: '',
    phone: '',
    source: 'pilot_page' as PilotLeadSource,
    status: 'new' as PilotLeadStatus,
    priority: 'normal' as PilotLeadPriority,
    tags: '',
    notes: '',
    nextActionAt: '',
    ownerUserId: profile?.id || '',
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) {
        throw new Error('Missing user profile');
      }
      await PilotCrmService.createLead(
        {
          companyName: formData.companyName,
          industry: formData.industry || undefined,
          employeeCountRange: (formData.employeeCountRange as any) || undefined,
          website: formData.website || undefined,
          hqState: formData.hqState || undefined,
          contactName: formData.contactName || undefined,
          contactEmail: formData.contactEmail,
          contactRole: formData.contactRole || undefined,
          phone: formData.phone || undefined,
          source: formData.source,
          status: formData.status,
          priority: formData.priority,
          tags: formData.tags
            ? formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
            : [],
          notes: formData.notes || undefined,
          nextActionAt: formData.nextActionAt ? new Date(formData.nextActionAt) : undefined,
          ownerUserId: formData.ownerUserId || undefined,
        },
        profile.id
      );
    },
    onSuccess: () => {
      onCreated();
    },
    onError: (err: any) => {
      setError(err?.message || 'Failed to create lead');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <Card className="relative max-w-3xl w-full z-10">
          <div className="flex items-center gap-2 mb-6">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">New Pilot Lead</h2>
          </div>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Company Name"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              />
              <FormInput
                label="Industry"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              />
              <FormSelect
                label="Employee Count"
                options={[
                  { value: '', label: 'Select range' },
                  ...EMPLOYEE_COUNT_RANGES.map((range) => ({ value: range, label: range })),
                ]}
                value={formData.employeeCountRange}
                onChange={(e) => setFormData({ ...formData, employeeCountRange: e.target.value })}
              />
              <FormInput
                label="Website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
              <FormInput
                label="HQ State"
                value={formData.hqState}
                onChange={(e) => setFormData({ ...formData, hqState: e.target.value })}
              />
              <FormInput
                label="Contact Name"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              />
              <FormInput
                label="Contact Email"
                type="email"
                required
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              />
              <FormInput
                label="Contact Role"
                value={formData.contactRole}
                onChange={(e) => setFormData({ ...formData, contactRole: e.target.value })}
              />
              <FormInput
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <FormSelect
                label="Source"
                options={PILOT_LEAD_SOURCES.map((source) => ({
                  value: source,
                  label: source.replace('_', ' '),
                }))}
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value as PilotLeadSource })}
              />
              <FormSelect
                label="Status"
                options={PILOT_LEAD_STATUSES.map((status) => ({
                  value: status,
                  label: status.replace('_', ' '),
                }))}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as PilotLeadStatus })}
              />
              <FormSelect
                label="Priority"
                options={PILOT_LEAD_PRIORITIES.map((priority) => ({
                  value: priority,
                  label: priority,
                }))}
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as PilotLeadPriority })}
              />
              <FormSelect
                label="Owner"
                options={ownerOptions}
                value={formData.ownerUserId || 'unassigned'}
                onChange={(e) =>
                  setFormData({ ...formData, ownerUserId: e.target.value === 'unassigned' ? '' : e.target.value })
                }
              />
              <FormInput
                label="Next Action"
                type="datetime-local"
                value={formData.nextActionAt}
                onChange={(e) => setFormData({ ...formData, nextActionAt: e.target.value })}
              />
            </div>
            <FormInput
              label="Tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="comma, separated, tags"
            />
            <FormTextarea
              label="Notes"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" isLoading={createMutation.isPending}>
                Create Lead
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
